import { cva, type VariantProps } from 'class-variance-authority';
import Link from 'next/link';

import { onest } from '@/styles/fonts';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground duration-300 ',
        primary: cn(
          `${onest.className} transform h-[48px] w-full rounded-full bg-rm-secondary-100 py-3 text-center text-[1rem] font-semibold text-white transition-all duration-200 md:max-w-[305px] lg:hover-custom-scale lg:text-[1rem]`
        ),
        secondary: cn(
          `${onest.className} w-full rounded-full h-[48px] border border-2 text-center text-[1rem] font-normal border-rm-neutral-300 bg-white py-3 transition-all duration-200 md:max-w-[305px] lg:hover-custom-scale lg:text-[1rem]`
        ),
        dark: 'bg-rm-secondary-100 text-white rounded-full w-full py-3 text-center text-[1rem] lg:text-lg font-semibold',
        light:
          'bg-white shadow-md text-rm-neutral-700 rounded-full w-full py-3 text-center border border-rm-neutral-200 text-[1rem] lg:text-lg font-normal',
      },
      size: {
        default: 'px-4 py-3',
        sm: 'h-9 px-3',
        lg: 'h-11 px-4',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  href: string;
  children: React.ReactNode;
}

const CustomLink = ({
  href,
  variant,
  className,
  size,
  children,
  ...props
}: ButtonProps) => {
  return (
    <Link
      href={href}
      {...props}
      className={cn(buttonVariants({ variant, size, className }))}
    >
      {children}
    </Link>
  );
};

export default CustomLink;
